import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Logo from "../components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await login(email, password);
      toast.success("Bem-vindo!");
      nav(res?.role === "admin" ? "/admin" : "/painel");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 grid-bg">
      <div className="hidden md:flex relative flex-col justify-between p-10 overflow-hidden border-r border-slate-800">
        <div>
          <div>
            <Logo />
            <div className="mt-2 text-[11px] mono uppercase tracking-[0.3em] text-orange-400/80">
              área do cliente · portal de orçamentos
            </div>
          </div>
        </div>

        <div>
          <h1 className="display text-4xl lg:text-5xl font-bold leading-tight">
            Precisão em <span className="text-orange-400">cada corte</span>,<br />
            confiança em <span className="text-amber-300">cada orçamento</span>.
          </h1>
          <p className="mt-4 text-slate-400 max-w-md text-sm leading-relaxed">
            Leia arquivos DXF, calcule metros de corte, quantidade de furações e
            gere orçamentos profissionais em PDF em segundos.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[
              { k: "DXF", v: "auto" },
              { k: "m corte", v: "0.001" },
              { k: "PDF", v: "1 clique" },
            ].map((s) => (
              <div key={s.k} className="card-tactical rounded-md p-3">
                <div className="text-xs text-slate-400 uppercase tracking-wider">{s.k}</div>
                <div className="mono text-orange-300 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-[11px] mono uppercase tracking-widest text-slate-500">
          v1.0 · industrial edition
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <form
          onSubmit={submit}
          className="w-full max-w-sm card-tactical rounded-xl p-7 shadow-2xl"
        >
          <div className="md:hidden mb-6">
            <Logo compact />
          </div>

          <h2 className="display text-2xl font-bold">Entrar</h2>
          <p className="text-sm text-slate-400 mt-1 mb-6">
            Acesse seu painel de orçamentos
          </p>

          <label className="text-xs uppercase tracking-wider text-slate-400">E-mail</label>
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-1 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition"
          />

          <label className="text-xs uppercase tracking-wider text-slate-400 mt-4 block">
            Senha
          </label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="mt-1 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition"
          />

          {error && (
            <div
              className="mt-3 text-xs text-red-300 bg-red-950/40 border border-red-900 rounded px-2 py-1.5"
              data-testid="login-error-message"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="mt-6 w-full pill-accent rounded-md py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:opacity-95 disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Entrar no sistema
          </button>

          <div className="mt-4 text-[11px] mono text-slate-500 text-center">
            Acesso restrito a clientes e equipe Alpha Metais
          </div>
        </form>
      </div>
    </div>
  );
}
